import { Accept, DropzoneOptions, useDropzone } from 'react-dropzone';
import { ReactNode, useCallback, useId, useState } from 'react';
import { FileListItem, FormHelperText, InputLabel, List, Stack, styled } from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { useController } from 'react-hook-form';
import { useFieldStateError } from '@razz-team/ui-web/src/components/Form/useFieldStateError';
import { DefaultError, UseMutationResult } from '@tanstack/react-query';
import { ApiUploadFileResponse } from '../../openapi';
import { sanitizeFileName } from '@razz-team/utils';

export type FileDropZoneProps = {
  name: string;
  label: ReactNode;
  accept?: Accept;
  minSize?: number;
  maxSize?: number;
  maxFiles?: number;
  required?: boolean;
  disabled?: boolean;
  options?: DropzoneOptions;
  mutation: UseMutationResult<ApiUploadFileResponse, DefaultError, FormData>;
};

const DropZone = styled('div', {
  name: 'DropZone',
  shouldForwardProp: (propName) => propName !== 'active',
})<{ active?: boolean }>(({ theme }) => ({
  background: theme.palette.background.default,
  minHeight: 64,
  display: 'flex',
  textAlign: 'center',
  alignItems: 'center',
  alignContent: 'center',
  justifyContent: 'center',
  border: `2px dashed ${theme.palette.tertiary.light}`,
  borderWidth: 2,
  borderStyle: 'dashed',
  borderColor: theme.palette.tertiary.light,
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  transition: theme.transitions.create(['border-color']),
  '&:hover': {
    borderColor: theme.palette.tertiary.main,
  },
  variants: [
    {
      props: ({ active }) => active,
      style: {
        borderColor: theme.palette.primary.main,
      },
    },
  ],
}));

type FileState = {
  name: string;
  sanitized: string;
  size: number;
  type: string;
  url: string | undefined;
};

export function FileDropField({
  name,
  label,
  accept,
  minSize,
  maxSize,
  maxFiles,
  required,
  disabled,
  mutation,
}: FileDropZoneProps) {
  const id = useId();
  const { t } = useTranslation();
  const [files, setFiles] = useState<FileState[]>([]);

  const { field, fieldState } = useController({
    name,
    disabled,
    rules: {
      required,
    },
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    disabled,
    accept,
    minSize,
    maxSize,
    maxFiles: maxFiles ? maxFiles - files.length : undefined,
    onDrop: (accepted) => {
      setFiles((prev) => [
        ...prev,
        ...accepted.map((file) => ({
          name: file.name,
          sanitized: sanitizeFileName(file.name),
          size: file.size,
          type: file.type,
          url: undefined,
        })),
      ]);

      const formData = new FormData();
      accepted.forEach((file) => {
        const sanitized = sanitizeFileName(file.name);
        formData.append(sanitized, file, sanitized);
      });

      mutation.mutate(formData, {
        onSuccess(data) {
          setFiles((prev) => {
            const next = prev.map((file) => {
              const url = data.documents[file.sanitized];
              return url ? { ...file, url } : file;
            });

            field.onChange(next.map((file) => file.url).filter((url) => url !== undefined));

            return next;
          });
        },
      });
    },
    onDragLeave: field.onBlur,
    onFileDialogCancel: field.onBlur,
  });

  const error = useFieldStateError(label, fieldState.error);

  const handleRemoveFile = useCallback(
    (removedFile: Partial<FileState>) => {
      setFiles((prev) => {
        const next = prev.filter((file) => file.url !== removedFile.url);
        field.onChange(next.map((file) => file.url).filter((file) => file !== removedFile.url));
        return next;
      });
    },
    [field],
  );

  return (
    <Stack spacing={1}>
      <InputLabel htmlFor={id}>{label}</InputLabel>
      <DropZone {...getRootProps()} active={isDragActive}>
        <input {...getInputProps()} id={id} />
        <FormHelperText>
          {isDragActive ? t('Drop the files here') : t('Drop files here, or click to select files')}
        </FormHelperText>
      </DropZone>
      {error ? <FormHelperText error>{error}</FormHelperText> : null}
      <List disablePadding>
        {files.map((file) => (
          <FileListItem
            key={file.name}
            file={file}
            loading={!file.url}
            onRemove={handleRemoveFile}
          />
        ))}
      </List>
    </Stack>
  );
}
